import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView } from './ChartView'

/**
 * The ZIP code lookup chart: the average adjusted gross income on the tax
 * returns filed from any ZIP code, beside its state's and the U.S.'s, from the
 * IRS's ZIP code data. The page's own data (`chart.data`) is the ZIP code it
 * opens with; any other comes from GET /zip-codes/:zip.json (ZipCodesController).
 * `?zip=12345` in the address opens a ZIP code directly, and the address
 * follows each search so a result can be shared.
 */

const wholeNumber = new Intl.NumberFormat('en-US')
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function setAddress(zip) {
  const url = new URL(window.location.href)
  url.searchParams.set('zip', zip)
  window.history.replaceState(null, '', url)
}

export function ZipCodeLookup({ chart }) {
  const inputId = useId()
  const [result, setResult] = useState(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)

  const lookUp = async (raw) => {
    const zip = raw.trim()
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      setStatus({ loading: false, message: 'Type a 5-digit ZIP code, such as 60614.' })
      return
    }
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const response = await fetch(`/zip-codes/${zip.slice(0, 5)}.json`, { headers: { Accept: 'application/json' }, signal: controller.signal })
      if (response.status === 404) {
        setStatus({
          loading: false,
          message: `The IRS has no figures for ZIP code ${zip.slice(0, 5)}. It leaves out ZIP codes with fewer than 100 returns, single buildings and ZIP codes nobody lives in.`,
        })
        return
      }
      if (!response.ok) throw new Error(`Request failed (${response.status})`)
      const found = await response.json()
      setResult(found)
      setStatus({ loading: false, message: null })
      setAddress(found.zip)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That ZIP code couldn’t be loaded. Please try again.' })
    }
  }

  // Open the ZIP code in the address, if any.
  useEffect(() => {
    const zip = new URLSearchParams(window.location.search).get('zip')
    if (zip) lookUp(zip)
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const data = result
    ? [
        { label: `ZIP ${result.zip}`, value: result.average },
        { label: result.state_name, value: result.state_average },
        { label: 'United States', value: result.us_average },
      ]
    : chart.data

  return (
    <div className="w-full">
      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => { event.preventDefault(); lookUp(query) }}
      >
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="form-label">Find a ZIP code</label>
          <input
            id={inputId}
            type="search"
            inputMode="numeric"
            className="input-form-field"
            placeholder="A 5-digit ZIP code, e.g. 60614"
            autoComplete="postal-code"
            value={query}
            maxLength={10}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary min-h-10" disabled={status.loading}>
          {status.loading ? 'Loading…' : 'Show'}
        </button>
      </form>

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      {result ? (
        <p className="mb-4 text-sm text-ink-muted break-words">
          In tax year {result.tax_year}, {wholeNumber.format(result.returns)} returns were filed from ZIP
          code {result.zip} ({result.state_name}), reporting an average income of {dollars.format(result.average)}.
          That's higher than {result.percentile}% of the ZIP codes in the IRS data.
        </p>
      ) : null}

      <ChartView
        searchable={false}
        chart={{ ...chart, title: result ? `Average income on tax returns in ZIP code ${result.zip}` : chart.title, data }}
      />
    </div>
  )
}
